import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { decryptFromStoreLoc, encryptAndStoreLoc } from '../helper/localStorage';
import { PasswordValidateField } from '../validate/password';
import ErrorFilter from '../helper/errorFilter';
import { forgotPassword, verifyOtp } from '../controller';
import { toast } from 'react-toastify';
import { requestHandler } from '../helper';
import SingleError from '../helper/SingleError';

function VerifyEmail() {

  // Timer
  const [timeLeft, setTimeLeft] = useState(null); // Set initial timeLeft as null
  const navigate = useNavigate();
  const isValidate = decryptFromStoreLoc("isValidate");
  const requireField = ["otp"];
  const [errors, setErrors] = useState({});
  const [submitCount, setSubmitCount] = useState(0);
  const location = useLocation(); // Access location
  const email = location.state?.email; // Retrieve email from state

  useEffect(() => {
    const otpStartTime = decryptFromStoreLoc("otpStartTime");
    if (otpStartTime) {
      const elapsedTime = Math.floor(Date.now() / 1000) - otpStartTime;
      const remainingTime = 60 - elapsedTime;
      setTimeLeft(remainingTime > 0 ? remainingTime : 0); // Set remaining time or 0 if expired
    }

    if (timeLeft > 0) {
      const timerId = setInterval(() => {
        setTimeLeft((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
      }, 1000);
      return () => clearInterval(timerId);
    } else if (timeLeft === 0) {
      navigate("/reset-password");
      localStorage.clear();
    }
  }, [timeLeft, navigate]);

  // OTP Fill
  const [inputs, setInputs] = useState(Array(6).fill(""));
  const inputRefs = useRef([]);
  const handleKeyDown = (e, index) => {
    if (
      !/^[0-9]{1}$/.test(e.key) &&
      e.key !== "Backspace" &&
      e.key !== "Delete" &&
      e.key !== "Tab" &&
      !e.metaKey
    ) {
      e.preventDefault();
    }
    if (e.key === "Backspace" || e.key === "Delete") {
      if (inputs[index] !== "") {
        const newInputs = [...inputs];
        newInputs[index] = "";
        setInputs(newInputs);
      } else if (index > 0) {
        const newInputs = [...inputs];
        newInputs[index - 1] = "";
        setInputs(newInputs);
        inputRefs.current[index - 1].focus();
      }
    }
  };
  const handleInput = (e, index) => {
    const value = e.target.value;
    const newInputs = [...inputs];
    newInputs[index] = value;
    setInputs(newInputs);
    if (value && index < inputs.length - 1) {
      inputRefs.current[index + 1].focus();
    }
    if (submitCount > 0) {
      const otp = newInputs.join("");
      const updatedValues = { otp };
      let validationErrors = PasswordValidateField(updatedValues);
      validationErrors = ErrorFilter(validationErrors, requireField);

      if (Object.keys(validationErrors).length === 0) {
        delete errors.otp;
      }
    }
  };
  const handleFocus = (index) => {
    inputRefs.current[index].select();
  };
  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text");
    if (/^[0-9]{6}$/.test(text)) {
      const digits = text.split("");
      const newInputs = digits.map((digit, index) =>
        index < inputs.length ? digit : ""
      );
      setInputs(newInputs);
      inputRefs.current[inputs.length - 1].focus();
    }
  };

  useEffect(() => {
    if (isValidate !== 1) {
      navigate('/');
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    document.title = "Always Outdoors | OTP";
    // eslint-disable-next-line
  }, [window.location.pathname])

  const handleConfirmOtp = async (e) => {
    e.preventDefault();
    const otp = inputs.join("");
    setSubmitCount(prevCount => prevCount + 1);
    const updatedValues = { otp };
    let validationErrors = PasswordValidateField(updatedValues);
    validationErrors = ErrorFilter(validationErrors, requireField);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      await requestHandler(async () => await verifyOtp({ otp: updatedValues.otp, email: email }),
        null,
        (res) => {
          toast.success(res.message);
          encryptAndStoreLoc("isOtpValidate", 1);
          navigate("/create-password", { state: { email: email } });
          localStorage.removeItem('otpStartTime');
          localStorage.removeItem('isValidate');
        },
        () => { }
      )
    }
  }

  const resendCode = async (e) => {
    e.preventDefault();
    await requestHandler(async () => await forgotPassword({ email: email }),
      null,
      (res) => {
        toast.success(res.message);
        const newOtpStartTime = Math.floor(Date.now() / 1000);
        encryptAndStoreLoc("otpStartTime", newOtpStartTime); // Update otpStartTime in local storage
        setTimeLeft(60);
      },
      () => { }
    )
  }

  const backToLogin = () => {
    navigate("/");
    localStorage.clear();
  }

  return (
    <div className="login-wrapper">
      <div className="container">
        <Row className="justify-content-center">
          <Col xxl={5} xl={6} lg={7} md={9}>
            <div className="login-card">
              <div className="login-logo">
                <img
                  src="/images/svg/logo.svg"
                  className="img-fluid"
                  alt="logo"
                />
              </div>
              <h4>Verify Your Email</h4>
              <p>Please enter the 6 digit verification code we just sent to {email}</p>
              <div className='mb-3'>
                <div className="code-group theme-input" onPaste={handlePaste}>
                  {inputs.map((input, index) => (
                    <input
                      key={index}
                      className='form-control'
                      value={input}
                      maxLength="1"
                      ref={(el) => (inputRefs.current[index] = el)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onChange={(e) => handleInput(e, index)}
                      onFocus={() => handleFocus(index)}
                    />
                  ))}
                </div>
                <SingleError error={errors.otp} />
                <div className='d-sm-flex justify-content-between align-items-center'>
                  <div className='d-flex justify-content-center align-items-center gap-1'>
                    <p className='mb-0 fs-16'>Didn't received code?</p>
                    <Link className='fs-16 fw-500 text-primary' onClick={resendCode}>Resend</Link>
                  </div>
                  <div className='fs-16 fw-500 text-primary text-center mt-sm-0 mt-1'>
                    {timeLeft > 0 &&
                      `${Math.floor(timeLeft / 60)
                        ?.toString()
                        .padStart(2, "0")}:${(timeLeft % 60)
                          ?.toString()
                          .padStart(2, "0")}`}
                  </div>
                </div>
              </div>
              <div className='d-flex align-items-center gap-2'>
                <button type="button" onClick={backToLogin} className="btn btn-outline-primary w-100">  Back to Login</button>
                <button type="button" onClick={handleConfirmOtp} className="btn btn-primary w-100">
                  Verify OTP
                </button>
              </div>

            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default VerifyEmail;
